import { z } from 'zod';
import {
  ErrorCode,
  LogisticsCalculationEditableContentSchema,
  LogisticsCalculationPublicViewKind,
  LogisticsCalculationRateInputSchema,
  calculateLogisticsCalculation,
  type LogisticsCalculationCargoContent,
  type LogisticsCalculationContent,
  type LogisticsCalculationEditableContent,
  type LogisticsCargoCandidate,
  type LogisticsCalculationViewItem,
} from '@magimp/shared';
import { ApiClientError } from '../../api/errors.js';

const DAY_MS = 86_400_000;

/**
 * The document as the manager fills it in: the two rouble rates the sheet asks
 * for, and the yuan-per-dollar key only a legacy document still carries.
 *
 * @aiInvariant The rouble-per-yuan rate is required exactly when the form holds
 *   no `cnyPerUsdRate` — that is, when the document it was opened from has no
 *   rouble-per-yuan rate of its own. A legacy document may keep the yuan input
 *   empty, and `toWireContent` then writes its original rate back untouched.
 */
export const LogisticsCalculationFormSchema = LogisticsCalculationEditableContentSchema.extend({
  rubPerUsdRate: LogisticsCalculationRateInputSchema,
  rubPerCnyRate: LogisticsCalculationRateInputSchema.or(z.literal('')).optional(),
}).superRefine((values, ctx) => {
  if (values.cnyPerUsdRate === undefined && !values.rubPerCnyRate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['rubPerCnyRate'],
      message: 'rubPerCnyRate is required',
    });
  }
});
export type LogisticsCalculationFormValues = z.infer<typeof LogisticsCalculationFormSchema>;
/** The default credit period the document offers a customer. */
export const LOGISTICS_CALCULATION_PAY_DUE_DAYS = 7;

export function mskDate(offsetDays = 0): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(Date.now() + offsetDays * DAY_MS));
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export function emptyLogisticsCalculation(manager: {
  name: string;
  phone: string;
}): LogisticsCalculationFormValues {
  return {
    style: 'form',
    clientCompany: '',
    clientContactName: '',
    clientContactPhone: '',
    payDueDate: mskDate(LOGISTICS_CALCULATION_PAY_DUE_DAYS),
    managerName: manager.name,
    managerPhone: manager.phone,
    paymentLabel: 'novat',
    // WHY no `cnyPerUsdRate` key at all: its absence is what makes the sheet ask
    // for the rouble-per-yuan rate, and a blank document has no input for it.
    rubPerUsdRate: '',
    rubPerCnyRate: '',
    payableCurrency: 'RUB',
    // The sheet states each amount in the currency it is charged in and does not
    // print an all-rouble reference, so nothing asks for one.
    showAllRubReference: false,
    cargos: [],
    commonServices: [],
  };
}

/**
 * A registry cargo as a row of the sheet: who and how much, nothing priced.
 *
 * WHY no prefilled costs: every amount is now typed into the cell that prints
 * it, so a row seeded from a snapshot would hand the customer figures the
 * manager never looked at.
 */
export function candidateToCargo(
  candidate: LogisticsCargoCandidate,
): LogisticsCalculationCargoContent {
  return {
    sourceCargoArticleId: candidate.id,
    article: candidate.article,
    name: candidate.name,
    seats: candidate.seats,
    weight: candidate.weight,
    volume: candidate.volume,
    ...(candidate.transportType ? { transportType: candidate.transportType } : {}),
    ...(candidate.departedAt ? { departedAt: candidate.departedAt } : {}),
    ...(candidate.arrivedAt ? { arrivedAt: candidate.arrivedAt } : {}),
    lines: [],
  };
}

/**
 * Form values as the content a write stores.
 *
 * INVARIANT: a document with a typed rouble-per-yuan rate stores only the two
 * rouble rates. A document that carries only the legacy rate keeps it verbatim:
 * the yuan input is empty, and the rounded value the masthead shows as its
 * placeholder never becomes the rate the totals are computed from.
 */
export function toWireContent(
  values: LogisticsCalculationFormValues,
): LogisticsCalculationEditableContent {
  const { rubPerCnyRate, ...rest } = LogisticsCalculationFormSchema.parse(values);
  if (!rubPerCnyRate) return LogisticsCalculationEditableContentSchema.parse(rest);
  const { cnyPerUsdRate: _legacyRate, ...withoutLegacyRate } = rest;
  return LogisticsCalculationEditableContentSchema.parse({
    ...withoutLegacyRate,
    rubPerCnyRate,
  });
}

export function toCalculationContent(
  values: LogisticsCalculationFormValues,
  documentDate: string,
): LogisticsCalculationContent {
  return { ...toWireContent(values), documentDate, layoutVersion: 'v2' };
}

/**
 * A stored document as the form opens it.
 *
 * WHY the yuan-per-dollar key is dropped once a rouble-per-yuan rate exists:
 * the form's own rule reads its presence as "opened from a legacy document",
 * and documents saved during the staged rollout may carry both.
 */
export function persistedToEditableContent(
  content: LogisticsCalculationContent,
): LogisticsCalculationFormValues {
  const { cnyPerUsdRate, ...editable } =
    LogisticsCalculationEditableContentSchema.strip().parse(content);
  return {
    ...editable,
    ...(editable.rubPerCnyRate === undefined && cnyPerUsdRate !== undefined
      ? { cnyPerUsdRate }
      : {}),
    payableCurrency: editable.payableCurrency ?? 'RUB',
  };
}

export function calculateEditable(
  values: LogisticsCalculationFormValues,
  documentDate = mskDate(),
) {
  const content = toCalculationContent(values, documentDate);
  return { content, calculation: calculateLogisticsCalculation(content) };
}

/**
 * The cargo a refused save says another document is holding, or null for every
 * other failure.
 *
 * Matched on the typed code and the typed meta, never on message text: #2983
 * answers with an empty `meta` when the holder is not visible to this manager,
 * and that case has a row to leave alone rather than one to mark.
 */
export function heldCargoOf(
  error: unknown,
): { cargoArticleId: string | null; publicCode: string | null } | null {
  if (!(error instanceof ApiClientError)) return null;
  if (error.code !== ErrorCode.LOGISTICS_CALCULATION_CARGO_HELD) return null;
  const read = (key: string): string | null => {
    const value = error.meta?.[key];
    return typeof value === 'string' ? value : null;
  };
  return { cargoArticleId: read('cargoArticleId'), publicCode: read('publicCode') };
}

/**
 * WHY only page views: the document is printed by the customer's own browser,
 * so there is no server render to count. The wire still carries historical
 * `PDF_DOWNLOAD` rows and this deliberately ignores them.
 */
export function countPublicViews(items: readonly LogisticsCalculationViewItem[]): {
  htmlViews: number;
} {
  return {
    htmlViews: items.filter((item) => item.kind === LogisticsCalculationPublicViewKind.HTML_VIEW)
      .length,
  };
}
